/**
 * @format
 * Web entry point for react-native-web
 */

// CRITICAL: Suppress Firebase errors FIRST, before any Firebase code loads
// These must be the very first imports
import '~services/FirebaseDebug/earlySuppress';
import '~services/FirebaseDebug/firebaseSilencer';
import '~services/FirebaseDebug/prePatch';

// React DOM polyfill (must come before react-native-web imports)
import '~services/web/reactDomPolyfill';

import { decode as atob, encode as btoa } from 'base-64';

if (typeof global.atob === 'undefined') {
    global.atob = atob;
}
if (typeof global.btoa === 'undefined') {
    global.btoa = btoa;
}

// Initialize URL polyfill
import 'react-native-url-polyfill/auto';

// Initialize TextEncoder polyfill (required for QR code generation)
import 'text-encoding-polyfill';

// Load Stripe.js from CDN for web payment processing
function loadStripeJs() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.Stripe) {
      console.log('✅ [WEB] Stripe.js already loaded');
      resolve(window.Stripe);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => {
      console.log('✅ [WEB] Stripe.js loaded from CDN');
      resolve(window.Stripe);
    };
    script.onerror = () => {
      console.warn('⚠️ [WEB] Failed to load Stripe.js from CDN');
      resolve(null);
    };
    document.head.appendChild(script);
  });
}

// Expose Stripe public key to window for React components
function initializeStripeConfig() {
  if (typeof window !== 'undefined') {
    // Try to get from environment variable via webpack DefinePlugin
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || '';
    if (stripeKey) {
      window.__STRIPE_PUBLIC_KEY__ = stripeKey;
      console.log('✅ [WEB] Stripe public key configured');
    } else {
      console.warn('⚠️ [WEB] REACT_APP_STRIPE_PUBLIC_KEY not set in environment');
    }
  }
}

// Initialize configuration and load Stripe
initializeStripeConfig();
if (typeof document !== 'undefined') {
  loadStripeJs();
}

console.log('🌐 [WEB] Index.web.js loaded');

import React from 'react';
import { AppRegistry } from 'react-native';

// Initialize Firebase Web SDK BEFORE importing App component
// This is critical - Firebase must be initialized before any components that use it
import './src/services/firebase-web/config';

import App from './src/containers/Core/App';
import appConfig from './app.json';

// Import ReactDOM/client properly - this will give us createRoot
// We need to bypass the alias to get the real react-dom/client
let ReactDOM = null;
try {
  ReactDOM = require('react-dom/client');
  console.log('🌐 [WEB] Loaded react-dom/client directly');
} catch (e) {
  console.warn('🌐 [WEB] Could not load react-dom/client, trying react-dom');
  try {
    const ReactDOMModule = require('react-dom');
    ReactDOM = {
      createRoot: (container) => {
        if (ReactDOMModule.createRoot) {
          return ReactDOMModule.createRoot(container);
        }
        // Legacy React version
        return {
          render: (el) => ReactDOMModule.render(el, container),
          unmount: () => ReactDOMModule.unmountComponentAtNode(container),
        };
      },
    };
    console.log('🌐 [WEB] Using react-dom with manual createRoot wrapper');
  } catch (e2) {
    console.error('🌐 [WEB] FATAL: Could not load any ReactDOM');
  }
}

const appName = appConfig.name;
console.log('🌐 [WEB] About to register component with name:', appName);

// Register the app with AppRegistry
AppRegistry.registerComponent(appName, () => App);

// Function to initialize the app when DOM is ready
function initializeApp() {
  const rootElement = document.getElementById('root');
  
  console.log('🌐 [WEB] initializeApp called');
  console.log('🌐 [WEB] Root element:', rootElement);
  
  if (!rootElement) {
    console.error('🌐 [WEB] ERROR: Root element #root not found!');
    document.body.innerHTML = '<div style="color: red; font-size: 20px; padding: 20px;">ERROR: Root element #root not found</div>';
    return;
  }
  
  console.log('🌐 [WEB] Root element found, rendering App component');
  
  try {
    console.log('🌐 [WEB] ReactDOM object:', ReactDOM);
    console.log('🌐 [WEB] ReactDOM.createRoot:', ReactDOM?.createRoot);
    
    if (!ReactDOM || !ReactDOM.createRoot) {
      throw new Error('ReactDOM.createRoot is not available');
    }
    
    console.log('🌐 [WEB] Creating React root');
    const root = ReactDOM.createRoot(rootElement);
    
    console.log('🌐 [WEB] Rendering App component');
    root.render(React.createElement(App));
    
    console.log('✅ [WEB] React app rendered successfully!');
    
    // Verify rendering
    setTimeout(() => {
      const hasContent = rootElement.innerHTML.length > 0;
      const childCount = rootElement.children.length;
      console.log(`✅ [WEB] Verification: innerHTML length=${rootElement.innerHTML.length}, children=${childCount}`);
      if (!hasContent) {
        console.error('🔴 [WEB] WARNING: Root element is still empty after render!');
        console.error('🔴 [WEB] This suggests App component rendered null or empty Fragment');
      }
    }, 500);
  } catch (error) {
    console.error('🔴 [WEB] Error rendering app:', error?.message);
    console.error('🔴 [WEB] Stack:', error?.stack);
    rootElement.innerHTML = '<div style="color: red; font-size: 18px; padding: 20px;"><strong>Error:</strong> ' + (error?.message || 'Unknown error') + '</div>';
  }
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  console.log('🌐 [WEB] DOM still loading, waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  console.log('🌐 [WEB] DOM already loaded, initializing app immediately');
  // Use requestIdleCallback or setTimeout to defer execution slightly
  setTimeout(initializeApp, 0);
}

